import { useMutation, useQuery } from '@apollo/client';
import { FlashMessage } from '@bnnvara/flash-message';
import { BaseForm } from '@bnnvara/forms/src/new_components/BaseForm/BaseForm';
import { GridItem } from '@bnnvara/grids';
import { Loading } from '@bnnvara/icons/src/components/UserInterfaceIcons/Loading';
import { Heading } from '@bnnvara/typography';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import { useTheme } from 'styled-components';

import { UpdateNotificationSettings } from '../../../api/mutations/notifications.gql';
import { FetchNotificationSettings } from '../../../api/queries/me/notifications.gql';
import Link from '../../Link';
import { SettingFormFields } from './SettingFormFields';
import * as S from './styled';

export function EditorialNotificationSettings({ UID, inModal = false }) {
  const [message, setMessage] = useState(null);
  const theme = useTheme();
  const query = useQuery(FetchNotificationSettings, {
    variables: {
      UID,
    },
    fetchPolicy: 'no-cache',
  });
  const [updateNotificationSettings, mutation] = useMutation(
    UpdateNotificationSettings,
    {
      refetchQueries: [
        { query: FetchNotificationSettings, variables: { UID } },
      ],
      awaitRefetchQueries: true,
    }
  );
  const isDisabled = mutation.loading;

  const handleSubmit = useCallback(
    async (values) => {
      if (isDisabled) {
        return;
      }

      const result = await updateNotificationSettings({
        variables: {
          editorialNotificationPerEmail: values.editorialNotificationPerEmail,
          editorialNotificationInterval: values.editorialNotificationInterval,
        },
      });

      const isSuccessFullyChanged =
        result?.data?.updateNotificationSettings?.success;

      if (isSuccessFullyChanged) {
        setMessage((prev) => {
          return {
            error: false,
            text: 'Instellingen gewijzigd',
            count: prev?.count ? prev.count + 1 : 1,
          };
        });
      } else {
        setMessage((prev) => {
          return {
            error: true,
            text: 'Er is iets misgegaan bij het wegschrijven van de settings',
            count: prev?.count ? prev.count + 1 : 1,
          };
        });
      }
    },
    [isDisabled, updateNotificationSettings]
  );

  return (
    <>
      <GridItem columnStartSm="1" columnEndSm="12">
        <Heading element="h2" variant="one">
          Jouw instellingen
        </Heading>
      </GridItem>
      <GridItem columnStartSm="1" columnEndSm="12">
        <Heading element="h3" variant="three">
          Volgen van BNNVARA titels / thema&lsquo;s
        </Heading>
      </GridItem>
      <GridItem columnStartSm="1" columnEndSm="12">
        <S.ContentWrapper>
          <GridItem columnStartSm="1" columnEndSm="12">
            {query.loading && !inModal && <p>De settings worden geladen</p>}
            {query.loading && inModal && <Loading />}
            {query.error && (
              <p>Er is een probleem met het ophalen van de settings</p>
            )}
          </GridItem>
          {query.data?.me?.notificationSettings && (
            <S.FormContainer isDisabled={isDisabled}>
              <BaseForm
                id="editorial-notification-settings-form"
                onSubmit={handleSubmit}
              >
                <SettingFormFields
                  editorialNotificationPerEmail={
                    query.data.me.notificationSettings
                      .editorialNotificationPerEmail
                  }
                  editorialNotificationInterval={
                    query.data.me.notificationSettings
                      .editorialNotificationInterval
                  }
                />
                {inModal && (
                  <S.SettingsFooterText>
                    De notificatie-instellingen voor titels en/of thema&lsquo;s
                    die je volgt kun je beheren op je{' '}
                    <Link href="/profiel">profiel</Link>.
                  </S.SettingsFooterText>
                )}
              </BaseForm>
            </S.FormContainer>
          )}
        </S.ContentWrapper>
        {message && (
          <FlashMessage
            key={message.count}
            message={message.text}
            color={message.error ? theme.error.main : theme.success.main}
          />
        )}
      </GridItem>
    </>
  );
}

EditorialNotificationSettings.propTypes = {
  UID: PropTypes.string.isRequired,
  inModal: PropTypes.bool,
};
